/**
 * Whether a `nodes` realtime event belongs to what this tab is showing.
 *
 * The store holds one organization's nodes, so an event for a node of another org must not leak
 * in. That is the whole rule. It was first written as "the event's network is in the loaded
 * networks", and the defect below is from that time.
 *
 * ⚠⚠ THAT SPELLING ALSO DROPS AN EVENT ABOUT A NODE THE STORE ALREADY HOLDS (app#366), and a
 * dropped event is permanent: nothing re-reads `nodes` short of a reload, so the page keeps a
 * stale row for the life of the tab. A held node was loaded by the org-scoped query, so applying
 * its update leaks nothing — the org filter has already been passed, once, by the row itself.
 * This matters when the loaded networks are momentarily incomplete or the payload is short of a
 * `network_id`: the node on screen is exactly the one whose update must not be lost.
 *
 * ⚠ A node that is NOT held still has to clear the org filter, which is what keeps another
 * org's node off the screen. The filter is the one `useNode.get()` reads with: the node's TENANT
 * (`owner_org_id`) is the active org (G-1, app#385 and app#388, docs/PERMISSIONS.md § 2 and § 4
 * "see"). The org of the node's network does not count: a node of another org in one of the active
 * org's networks is that org's, and a node of the active org in another org's network is ours.
 */
export function shouldApplyNodeEvent(params: {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE'
  activeOrgId: string | null | undefined
  heldNodeIds: string[]
  nodeId: string | null | undefined
  /** `nodes.owner_org_id` of the event's row. A payload that does not carry it cannot be placed. */
  ownerOrgId?: string | null
}): boolean {
  const { eventType, activeOrgId, heldNodeIds, nodeId, ownerOrgId } = params
  // A tombstone carries no row to scope, and removing a node the store does not hold is a no-op.
  if (eventType === 'DELETE') return true
  if (!activeOrgId) return true
  if (nodeId && heldNodeIds.includes(nodeId)) return true
  return !!ownerOrgId && ownerOrgId === activeOrgId
}
